import { Component, computed, input, output } from '@angular/core';

@Component({
  selector: 'app-loading-overlay',
  standalone: false,
  templateUrl: './loading-overlay.component.html',
  styleUrl: './loading-overlay.component.scss'
})
export class LoadingOverlayComponent {
  // =============================================================================
  // Loading State Inputs
  // =============================================================================

  /**
   * Controls the visibility of the loading overlay
   */
  visible = input<boolean>(false);

  /**
   * Current loaded value for progress calculation
   */
  currentLoaded = input<number>(0);

  /**
   * Total value for progress calculation
   */
  total = input<number>(100);

  /**
   * Translation key for the loading message
   * @default 'common.loading.preparing_data'
   */
  messageKey = input<string>('common.loading.preparing_data');

  /**
   * Controls the visibility of the progress bar
   * @default true
   */
  showProgress = input<boolean>(true);

  /**
   * Shows an indeterminate progress animation when true
   * Use when you can't track specific progress percentage
   * @default false
   */
  indeterminate = input<boolean>(false);

  // =============================================================================
  // Error State Inputs
  // =============================================================================

  /**
   * Controls whether to show error state instead of loading state
   * @default false
   */
  hasError = input<boolean>(false);

  /**
   * Translation key for the main error title
   * @default 'error.loading.title'
   */
  errorTitleKey = input<string>('error.loading.title');

  /**
   * Translation key for the error description
   * @default 'error.loading.description'
   */
  errorDescriptionKey = input<string>('error.loading.description');

  /**
   * Translation key for the suggestions box heading
   * @default 'error.loading.suggestions_heading'
   */
  errorSuggestionsHeadingKey = input<string>('error.loading.suggestions_heading');

  /**
   * Array of translation keys for suggestion items
   */
  errorSuggestionKeys = input<string[]>([
    'error.loading.suggestion.check_connection',
    'error.loading.suggestion.refresh_page',
    'error.loading.suggestion.try_again',
    'error.loading.suggestion.contact_support'
  ]);

  /**
   * Translation key for the primary action button (Refresh)
   * @default 'error.loading.action.refresh'
   */
  errorRefreshButtonKey = input<string>('error.loading.action.refresh');

  /**
   * Translation key for the secondary action button (Contact Support)
   * @default 'error.loading.action.contact_support'
   */
  errorContactButtonKey = input<string>('error.loading.action.contact_support');

  /**
   * Translation key for the reassurance message at the bottom
   * @default 'error.loading.reassurance'
   */
  errorReassuranceKey = input<string>('error.loading.reassurance');

  // =============================================================================
  // Outputs
  // =============================================================================

  /**
   * Emits when the user clicks the refresh button in error state
   */
  refresh = output<void>();

  /**
   * Emits when the user clicks the contact support button in error state
   */
  contactSupport = output<void>();

  // =============================================================================
  // Computed Properties
  // =============================================================================

  /**
   * Computed percentage for progress bar (0-100)
   */
  percentage = computed(() => {
    const total = this.total();
    const current = this.currentLoaded();
    if (total === 0) return 0;
    return Math.min(Math.round((current / total) * 100), 100);
  });

  // =============================================================================
  // Event Handlers
  // =============================================================================

  /**
   * Handle refresh button click
   */
  onRefresh(): void {
    this.refresh.emit();
  }

  /**
   * Handle contact support button click
   */
  onContactSupport(): void {
    this.contactSupport.emit();
  }
}
